// Custom Add to Cart Button Element
class CustomAddToCartButton extends HTMLElement {
  constructor() {
    super();
    this._handleClick = this._handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._handleClick);

    // Listen for theme:quick-add:open to reinitialize if needed
    document.addEventListener('theme:quick-add:open', () => {
      this.reinitialize();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick);
  }

  reinitialize() {
    // Method to reinitialize the button if needed
    this.classList.remove('is-loading');
    this.removeAttribute('aria-disabled');
  }

  async _handleClick(e) {
    e.preventDefault();

    const selectedOption = document.querySelector(
      '.subscription-content:not(.hidden) .delivery-option input:checked'
    );
    const selectedOptionData = selectedOption?.closest('product-delivery-option');
    if (!selectedOption) return;

    const addOnProduct = document.querySelector('.add-on-quantity');
    let extraFormData = null;

    if (addOnProduct) {
      const addOnProductId = addOnProduct.value;

      try {
        // Fetch current cart to check if add-on is already present
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const isAddOnInCart = cart.items.some(
          (item) => item.id == addOnProductId
        );

        if (!isAddOnInCart) {
          extraFormData = {
            id: addOnProductId,
            quantity: 1
          };
        }
      } catch (error) {
        console.error('Error checking cart for add-on product:', error);
      }
    }

    const product = {
      id: selectedOptionData.getAttribute('data-product-id'),
      variant: selectedOptionData.getAttribute('data-product-id'),
      sellingPlanId: selectedOptionData.getAttribute('data-selling-plan') || '',
      properties: {}
    };

    // Get quantity
    let quantity = 1;
    const quantityInput = document.querySelector('#quantity') ||
      selectedOption.querySelector('#custom_quantity_count');
    if (quantityInput) {
      quantity = parseInt(quantityInput.value, 10) || 1;
    }

    // Pass extraFormData only if it's defined
    if (extraFormData) {
      this.addToCart(product, quantity, extraFormData);
    } else {
      this.addToCart(product, quantity);
    }
  }

  addToCart(product, quantity = 1, extraFormData = null) {
    if (!product) {
      console.error('No product selected');
      return Promise.reject('No product selected');
    }

    // Prepare the form data
    const formData = new FormData();
    formData.append('id', product.variant || product.id);
    formData.append('quantity', quantity);

    // Add extra form data if available
    if (extraFormData) {
      formData.append('items[0][id]', extraFormData.id);
      formData.append('items[0][quantity]', extraFormData.quantity);
    }

    // Add selling plan if available
    if (product.sellingPlanId) {
      formData.append('selling_plan', product.sellingPlanId);
    }

    // Add properties if needed
    if (product.properties) {
      Object.entries(product.properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
    }

    // Request sections to update
    if (window.Shopify && window.Shopify.routes) {
      formData.append('sections', 'cart-icon-bubble,cart-drawer');
      formData.append('sections_url', window.location.pathname);
    }

    // Fetch configuration
    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    };

    // Show loading state
    this.classList.add('is-loading');
    this.setAttribute('aria-disabled', true);

    // Send the request
    return fetch(window.Shopify.routes.cart_add_url || '/cart/add.js', config)
      .then(response => response.json())
      .then(response => {
        if (response.status) {
          // Error occurred
          throw new Error(response.description || 'Error adding item to cart');
        }

        // Update cart UI
        this.updateCartUI(response);
        return response;
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        alert(error.message || 'Could not add item to cart. Please try again.');
        throw error;
      })
      .finally(() => {
        // Reset button state
        this.classList.remove('is-loading');
        this.removeAttribute('aria-disabled');
      });
  }

  updateCartUI(cartData) {
    // Close the quick-add drawer
    const quickAddDrawer = document.querySelector('.drawer.product-quick-add');
    if (quickAddDrawer) {
      quickAddDrawer.setAttribute('closing', '');
      // Dispatch close event for any listeners
      const closeEvent = new CustomEvent('theme:drawer:close', {
        bubbles: true,
        detail: { drawer: quickAddDrawer }
      });
      quickAddDrawer.dispatchEvent(closeEvent);
    }

    // Open cart drawer if it exists
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.handleAddedItem === 'function') {
      cartDrawer.handleAddedItem(cartData);
    }
  }
}

// Custom Product Delivery Option Element
class ProductDeliveryOption extends HTMLElement {
  constructor() {
    super();
    this._handleClick = this._handleClick.bind(this);
    this._handleRadioChange = this._handleRadioChange.bind(this);
  }

  connectedCallback() {
    this.radioInput = this.querySelector('input[type="radio"]');

    if (this.radioInput) {
      this.addEventListener('click', this._handleClick);
      this.radioInput.addEventListener('change', this._handleRadioChange);
    }

    // Listen for tab switching events
    document.addEventListener('subscription:tab:switched', () => {
      setTimeout(() => this._updateForm(), 0);
    });

    // Listen for theme:quick-add:open to reinitialize
    document.addEventListener('theme:quick-add:open', () => {
      this._updateForm();
    });

    // Initial update if this option is checked
    if (this.radioInput?.checked) {
      this._updateForm();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick);
    if (this.radioInput) {
      this.radioInput.removeEventListener('change', this._handleRadioChange);
    }
  }

  _handleClick(e) {
    // Don't trigger if click was on the radio input itself
    if (e.target.type === 'radio') return;

    if (this.radioInput) {
      this.radioInput.checked = true;
      this.radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      this._updateForm();
    }
  }

  _handleRadioChange(e) {
    this._updateForm();
  }

  _updateForm() {
    if (!this.radioInput?.checked) return;

    const productForm = document.querySelector('.product-form');
    if (!productForm) return;

    // Get the product and variant IDs
    const productId = this.getAttribute('data-product-id');
    const variantId = this.getAttribute('data-variant-id') || productId;
    const sellingPlanId = this.getAttribute('data-selling-plan');
    const sellingPlanName = this.getAttribute('data-selling-plan-name');

    // Update form values
    const idInput = productForm.querySelector('input[name="id"]');
    const sellingPlanInput = productForm.querySelector('input[name="selling_plan"]');
    const optionInput = productForm.querySelector('input[name="option-0"]');
    const quantityInput = productForm.querySelector('input[name="quantity"]');

    if (idInput) idInput.value = variantId;
    if (sellingPlanInput) sellingPlanInput.value = sellingPlanId || '';
    if (optionInput) optionInput.value = sellingPlanName || '';

    // Set quantity based on product type
    let quantity = 1;
    if (this.dataset.productOtp === 'true') {
      quantity = parseInt(this.dataset.quantity || '1', 10);
      if (quantityInput) quantityInput.value = quantity;
    } else {
      if (quantityInput) quantityInput.value = sellingPlanName === undefined ? 1 : 0;
    }

    // Update the price
    this._updatePriceDisplay(quantity, productId);

    // Dispatch event to update product display
    const event = new CustomEvent('product:variant:change', {
      detail: {
        productId: productId,
        variantId: variantId,
        sellingPlanId: sellingPlanId,
        sellingPlanName: sellingPlanName
      }
    });
    document.dispatchEvent(event);
  }

  _updatePriceDisplay(quantity, productId) {
    let priceValue = parseFloat(this.getAttribute('data-price') || '0');
    let compareAtPrice = null;

    // Check if there's a compare-at price available
    if (this.querySelector('.price-original')) {
      let originalPriceEl = this.querySelector('.price-original');
      compareAtPrice = parseFloat(this.getAttribute('data-full-price') ||
        originalPriceEl.textContent.trim().replace(/[^0-9\.]/g, ''));
    }

    // Update price display
    if (this.dataset.productOtp === 'true') {
      if (window.DisplayPrice) {
        window.DisplayPrice(
          priceValue / 100,
          compareAtPrice !== null && compareAtPrice !== priceValue ?
            (compareAtPrice / 100) * quantity : undefined,
          quantity,
          productId
        );
      }
    } else if (this.dataset.packSize !== null) {
      if (window.DisplayPrice) {
        window.DisplayPrice(
          (priceValue / 100) * this.dataset.packSize,
          compareAtPrice !== null && compareAtPrice !== priceValue ?
            (compareAtPrice / 100) * this.dataset.packSize : undefined,
          quantity,
          productId
        );
      }
    }
  }
}

// Register custom elements
customElements.define('custom-add-to-cart-button', CustomAddToCartButton);
customElements.define('product-delivery-option', ProductDeliveryOption);

document.addEventListener('theme:quick-add:open', function(event) {
  // Initialize any new custom elements that might have been added
  updateFormFromSelectedOption();
});

// Comprehensive initialization function
function initializePurchaseOptions() {
  console.log('Initializing purchase options...');

  // First, try to find and select a default option if none is selected
  const checkedOption = document.querySelector('.option-radio:checked, input[type="radio"]:checked');
  if (!checkedOption) {
    console.log('No option selected, attempting to select default...');

    // Try to find the first available option and select it
    const firstOption = document.querySelector('.option-radio:first-of-type, input[type="radio"]:first-of-type');
    if (firstOption) {
      firstOption.checked = true;
      console.log('Selected first available option');
    }
  }

  // Now update the form
  updateFormFromSelectedOption();
}

// MutationObserver to watch for DOM changes and reinitialize if needed
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver(function(mutations) {
    let shouldReinitialize = false;

    mutations.forEach(function(mutation) {
      // Check if purchase options were added or modified
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1 && // Element node
              (node.classList?.contains('product-form') ||
               node.querySelector?.('.product-form') ||
               node.classList?.contains('delivery-option') ||
               node.querySelector?.('.delivery-option'))) {
            shouldReinitialize = true;
          }
        });
      }
    });

    if (shouldReinitialize) {
      console.log('DOM changes detected, reinitializing purchase options...');
      setTimeout(() => {
        initializePurchaseOptions();
      }, 100);
    }
  });

  // Start observing when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  });
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use CustomAddToCartButton element instead
 */
function addToCart(product, quantity = 1, extraFormData = null) {
  console.warn('addToCart function is deprecated. Use custom-add-to-cart-button element instead.');

  // Find a custom add to cart button to delegate to
  const customButton = document.querySelector('custom-add-to-cart-button');
  if (customButton) {
    return customButton.addToCart(product, quantity, extraFormData);
  }

  console.error('No custom-add-to-cart-button element found');
  return Promise.reject('No custom add to cart button available');
}


//handle initial inputs value on load
function updateFormFromSelectedOption(product_option_selected = null) {
  console.log('updateFormFromSelectedOption called', { product_option_selected });

  // If no option provided, find the checked one with multiple fallback selectors
  if (!product_option_selected) {
    // Try multiple selectors to find the selected option
    const selectors = [
      '.subscription-content:not(.hidden) .delivery-option input:checked',
      '.subscription-content .delivery-option input:checked',
      '.delivery-option input:checked',
      'product-delivery-option input:checked',
      '.option-radio:checked'
    ];

    for (const selector of selectors) {
      product_option_selected = document.querySelector(selector);
      if (product_option_selected) {
        console.log('Found selected option with selector:', selector);
        break;
      }
    }

    if (!product_option_selected) {
      console.warn('No selected option found with any selector');

      // Fallback: try to select the first available option
      const firstOptionSelectors = [
        '.subscription-content:not(.hidden) .delivery-option input[type="radio"]:first-of-type',
        '.subscription-content .delivery-option input[type="radio"]:first-of-type',
        '.delivery-option input[type="radio"]:first-of-type',
        'product-delivery-option input[type="radio"]:first-of-type',
        '.option-radio:first-of-type'
      ];

      for (const selector of firstOptionSelectors) {
        const firstOption = document.querySelector(selector);
        if (firstOption) {
          console.log('Selecting first available option with selector:', selector);
          firstOption.checked = true;
          product_option_selected = firstOption;
          break;
        }
      }

      if (!product_option_selected) {
        console.warn('No options found at all');
        return;
      }
    }
  }

  let product_form = document.querySelector('.product-form');
  if (!product_form) {
    console.warn('Product form not found');
    return;
  }

  let productOptionSelectedData = product_option_selected.closest('.delivery-option') ||
                                 product_option_selected.closest('product-delivery-option');

  if (!productOptionSelectedData) {
    console.warn('Product option selected data not found');
    return;
  }

  // Get the product and variant IDs
  const productId = productOptionSelectedData.parentNode.getAttribute('data-product-id');
  const variantId = productOptionSelectedData.parentNode.getAttribute('data-variant-id') || productId;
  const sellingPlanId = productOptionSelectedData.parentNode.getAttribute('data-selling-plan');
  const sellingPlanName = productOptionSelectedData.parentNode.getAttribute('data-selling-plan-name');

  console.log('Updating form with:', { productId, variantId, sellingPlanId, sellingPlanName });

  // Update form values with null checks
  const idInput = product_form.querySelector('input[name="id"]');
  const sellingPlanInput = product_form.querySelector('input[name="selling_plan"]');
  const optionInput = product_form.querySelector('input[name="option-0"]');
  const quantityInput = product_form.querySelector('input[name="quantity"]');

  if (idInput && variantId) idInput.value = variantId;
  if (sellingPlanInput) sellingPlanInput.value = sellingPlanId || '';
  if (optionInput) optionInput.value = sellingPlanName || '';

  // Set quantity based on product type
  let quantity = 1;
  if (productOptionSelectedData.dataset.productOtp === 'true') {
    quantity = parseInt(productOptionSelectedData.dataset.quantity || '1', 10);
    if (quantityInput) quantityInput.value = quantity;
  } else {
    if (quantityInput) quantityInput.value = sellingPlanName === undefined ? 1 : 0;
  }

  console.log('Form inputs updated:', {
    id: idInput?.value,
    selling_plan: sellingPlanInput?.value,
    option_0: optionInput?.value,
    quantity: quantityInput?.value
  });

  // Update the price
  let priceValue = parseFloat(productOptionSelectedData.getAttribute('data-price') || '0');
  let compareAtPrice = null;

  // Check if there's a compare-at price available
  if (productOptionSelectedData.querySelector('.price-original')) {
    let originalPriceEl = productOptionSelectedData.querySelector('.price-original');
    compareAtPrice = parseFloat(productOptionSelectedData.getAttribute('data-full-price') || originalPriceEl.textContent.trim().replace(/[^0-9\.]/g, ''));
  }

  // Update price display
  if (productOptionSelectedData.dataset.productOtp === 'true') {
    DisplayPrice(
      priceValue / 100,
      compareAtPrice !== null && compareAtPrice !== priceValue ? (compareAtPrice / 100) * quantity : undefined,
      quantity,
      productId
    );
  } else if (productOptionSelectedData.dataset.packSize !== null) {
    DisplayPrice(
      (priceValue / 100) * productOptionSelectedData.dataset.packSize,
      compareAtPrice !== null && compareAtPrice !== priceValue ? (compareAtPrice / 100) * productOptionSelectedData.dataset.packSize : undefined,
      quantity,
      productId
    );
  }

  // Dispatch event to update product display
  const event = new CustomEvent('product:variant:change', {
    detail: {
      productId: productId,
      variantId: variantId,
      sellingPlanId: sellingPlanId,
      sellingPlanName: sellingPlanName
    }
  });
  document.dispatchEvent(event);
}

class ProductGiftDisplay extends HTMLElement {
  constructor() {
    super();
    this.isTiered = this.classList.contains('gift-list--tiered');
  }

  connectedCallback() {
    // Listen for product selection changes
    document.addEventListener('subscription:tab:switched', () => this.updateGifts());
    document.addEventListener('variant:changed', () => this.updateGifts());
    document.addEventListener('product:variant:change', () => this.updateGifts());

    const radioInputs = document.querySelectorAll('.delivery-option input[type="radio"]');
    radioInputs.forEach(input => {
      input.addEventListener('change', () => this.updateGifts());
    });

    // Initial update
    this.updateGifts();
  }

  disconnectedCallback() {
    document.removeEventListener('subscription:tab:switched', this.updateGifts);
    document.removeEventListener('variant:changed', this.updateGifts);
    document.removeEventListener('product:variant:change', this.updateGifts);
  }

  updateGifts() {
    let selectedOption = document.querySelector('.subscription-content:not(.hidden) .delivery-option input:checked');
    if (this.dataset.rule) selectedOption = document.querySelector('#subscribe-content:not(.hidden) .delivery-option input:checked');

    // If no option is selected, hide all gifts
    if (!selectedOption) {
      this._hideAllGifts();
      return;
    }

    const optionElement = selectedOption.closest('.delivery-option');
    const productId = optionElement.getAttribute('data-product-id');
    const isSubscription = optionElement.getAttribute('data-product-otp') !== 'true';

    // Show the gift display container
    this.classList.remove('hidden');

    if (this.isTiered) {
      this._handleTieredGifts(optionElement);
    } else {
      this._handleOptionBasedGifts(productId, isSubscription);
    }
  }

  _handleOptionBasedGifts(productId, isSubscription) {
    const gifts = this.querySelectorAll('.gift-option');
    const productGift = this.querySelector(`.gift-option[data-product-id="${productId}"]`);

    gifts.forEach(gift => {
      const giftProductId = gift.getAttribute('data-product-id');
      const isSubscriptionOnly = gift.getAttribute('data-subscription-only') === 'true';

      // Hide gifts that don't match the current product selection
      if (giftProductId && giftProductId !== productId) {
        gift.classList.add('hidden');
      } else {
        // If the gift is subscription-only, only show for subscriptions
        if (isSubscriptionOnly) {
          if (isSubscription) {
            gift.classList.remove('hidden');
          } else {
            gift.classList.add('hidden');
          }
        } else {
          // Show for both subscription and one-time purchase
          gift.classList.remove('hidden');
        }
      }
      gift.classList.remove('locked');
    });

    // Optionally, ensure only the correct product gift is shown for subscriptions
    if (productGift && isSubscription) {
      productGift.classList.remove('hidden');
    }
  }

  _handleTieredGifts(optionElement) {
    const selectedProductId = optionElement.getAttribute('data-product-id');
    const isSubscription = optionElement.getAttribute('data-product-otp') !== 'true';

    // Find the current gift level from the selected product
    const currentGiftLevel = this._getCurrentGiftLevel(selectedProductId);

    this.querySelectorAll('.gift-option').forEach(gift => {
      const alwaysActive = gift.classList.contains('always_active');
      const requiredProduct = gift.getAttribute('data-product');
      const giftLevel = parseInt(gift.getAttribute('data-gift-level') || '0', 10);

      // A gift should be unlocked if:
      // 1. It's marked as always active, OR
      // 2. Its required product matches the selected product, OR
      // 3. Its level is less than or equal to the current gift level
      // (Only for subscription products)
      let isUnlocked = alwaysActive ||
        requiredProduct === selectedProductId ||
        (currentGiftLevel > 0 && giftLevel <= currentGiftLevel) &&
        isSubscription;

      // Update gift display based on unlock status
      if (isUnlocked) {
        gift.classList.remove('locked');
        this._toggleGiftDisplay(gift, true);
      } else {
        gift.classList.add('locked');
        this._toggleGiftDisplay(gift, false);
      }
    });
  }

  _getCurrentGiftLevel(productId) {
    let highestLevel = 0;

    // Find all gifts that match this product and get the highest level
    this.querySelectorAll('.gift-option').forEach(gift => {
      if (gift.getAttribute('data-product') === productId) {
        const giftLevel = parseInt(gift.getAttribute('data-gift-level') || '0', 10);
        highestLevel = Math.max(highestLevel, giftLevel);
      }
    });

    return highestLevel;
  }

  _hideAllGifts() {
    // Only hide the container if there are no gifts to show
    const hasVisibleGifts = Array.from(this.querySelectorAll('.gift-option')).some(gift =>
      !gift.classList.contains('hidden') && !gift.classList.contains('locked')
    );

    if (!hasVisibleGifts) {
      this.classList.add('hidden');
    }
  }

  _toggleGiftDisplay(gift, unlocked) {
    const unlockEl = gift.querySelector('.unlock');
    const lockEl = gift.querySelector('.lock');

    if (unlockEl) unlockEl.style.display = unlocked ? 'flex' : 'none';
    if (lockEl) lockEl.style.display = unlocked ? 'none' : 'flex';
  }
}

// Register the custom element
customElements.define('product-gift-display', ProductGiftDisplay);


class SubscriptionTabs extends HTMLElement {
  constructor() {
    super();
    this._handleSubscribeClick = this._switchTab.bind(this, 'subscribe');
    this._handleOnetimeClick = this._switchTab.bind(this, 'onetime');
  }

  connectedCallback() {
    this.tabs = {
      subscribe: {
        button: this.querySelector('[data-tab="subscribe"]'),
        content: this.querySelector('#subscribe-content')
      },
      onetime: {
        button: this.querySelector('[data-tab="onetime"]'),
        content: this.querySelector('#onetime-content')
      }
    };

    if (!this.tabs.subscribe.button || !this.tabs.onetime.button) {
      console.warn('SubscriptionTabs: Tab elements not found');
      return;
    }

    // Add event listeners
    this.tabs.subscribe.button.addEventListener('click', this._handleSubscribeClick);
    this.tabs.onetime.button.addEventListener('click', this._handleOnetimeClick);

    // Initialize with the active tab (subscription is default)
    const activeTab = this.querySelector('.tab-option.active');
    if (activeTab) {
      this._switchTab(activeTab.getAttribute('data-tab'));
    } else {
      this._switchTab('subscribe');
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.tabs?.subscribe.button) {
      this.tabs.subscribe.button.removeEventListener('click', this._handleSubscribeClick);
    }
    if (this.tabs?.onetime.button) {
      this.tabs.onetime.button.removeEventListener('click', this._handleOnetimeClick);
    }
  }

  _switchTab(tabType) {
    const activeTab = this.tabs[tabType];
    const inactiveTab = this.tabs[tabType === 'subscribe' ? 'onetime' : 'subscribe'];

    // Update tab button states
    activeTab.button.classList.add('active');
    inactiveTab.button.classList.remove('active');

    // Show/hide content with !important to override any existing styles
    if (activeTab.content) {
      activeTab.content.classList.remove('hidden');
      activeTab.content.style.setProperty('display', 'block', 'important');
    }

    if (inactiveTab.content) {
      inactiveTab.content.classList.add('hidden');
      inactiveTab.content.style.setProperty('display', 'none', 'important');
    }

    // Toggle header visibility
    this.toggleSubscribeHeaders(tabType);

    // Select first option in the active tab
    const firstOption = activeTab.content?.querySelector('.delivery-option');
    const firstRadio = firstOption?.querySelector('input[type="radio"]');
    if (firstRadio && !firstRadio.checked) {
      firstRadio.checked = true;
      firstRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Dispatch event for external components
    this.dispatchEvent(new CustomEvent('subscription:tab:switched', {
      bubbles: true,
      detail: { tabType }
    }));
  }

  toggleSubscribeHeaders(tabType) {
    const subscribeHeaderSubscription = document.querySelector(
      '.subscribe-header--subscription'
    );
    const subscribeHeaderOtp = document.querySelector('.subscribe-header--otp');

    if (!subscribeHeaderSubscription || !subscribeHeaderOtp) return;

    if (tabType === 'subscribe') {
      subscribeHeaderSubscription.classList.remove('hidden');
      subscribeHeaderOtp.classList.add('hidden');
    } else if (tabType === 'onetime') {
      subscribeHeaderSubscription.classList.add('hidden');
      subscribeHeaderOtp.classList.remove('hidden');
    }
  }
}

// Register the custom element
customElements.define('subscription-tabs', SubscriptionTabs);

class CustomQuantity extends HTMLElement {
  constructor() {
    super();
    this.input = null;
    this.decreaseBtn = null;
    this.increaseBtn = null;
    this.min = 1;
    this.max = 99;
  }

  connectedCallback() {
    this.min = parseInt(this.getAttribute('min') || '1', 10);
    this.max = parseInt(this.getAttribute('max') || '99', 10);
    this.productId = this.getAttribute('product-id');

    this.render();
    this.setupEventListeners();
  }

  render() {
    this.innerHTML = `
      <div class="custom-quantity">
        <button type="button" class="quantity-btn decrease" aria-label="Decrease quantity">-</button>
        <input type="number" class="quantity-input" value="1" min="${this.min}" max="${this.max}" aria-label="Product quantity">
        <button type="button" class="quantity-btn increase" aria-label="Increase quantity">+</button>
      </div>
    `;

    this.input = this.querySelector('.quantity-input');
    this.decreaseBtn = this.querySelector('.decrease');
    this.increaseBtn = this.querySelector('.increase');
  }

  setupEventListeners() {
    this.decreaseBtn.addEventListener('click', () => this.updateQuantity(-1));
    this.increaseBtn.addEventListener('click', () => this.updateQuantity(1));
    this.input.addEventListener('change', () => this.handleInputChange());
  }

  updateQuantity(change) {
    let currentValue = parseInt(this.input.value, 10);
    let newValue = currentValue + change;

    // Ensure value is within min/max bounds
    newValue = Math.max(this.min, Math.min(this.max, newValue));

    if (newValue !== currentValue) {
      this.input.value = newValue;
      this.updateButtonStates();
      this.updateDeliveryOptionQuantity(newValue);
    }
  }

  handleInputChange() {
    let value = parseInt(this.input.value, 10) || this.min;

    // Validate min/max
    value = Math.max(this.min, Math.min(this.max, value));
    this.input.value = value;

    this.updateButtonStates();
    this.updateDeliveryOptionQuantity(value);
  }

  updateDeliveryOptionQuantity(quantity) {
    // Find the closest delivery option element and update its data-quantity attribute
    const deliveryOption = this.closest('.delivery-option');
    if (deliveryOption) {
      deliveryOption.setAttribute('data-quantity', quantity.toString());

      // Trigger form update with the current selected radio input
      const radioInput = deliveryOption.querySelector('input[type="radio"]');
      if (radioInput && radioInput.checked) {
        updateFormFromSelectedOption(radioInput);
      }
    }
  }

  updateButtonStates() {
    const value = parseInt(this.input.value, 10);
    this.decreaseBtn.disabled = value <= this.min;
    this.increaseBtn.disabled = value >= this.max;
  }
}

customElements.define('custom-quantity', CustomQuantity);

class SubscriptionInfoAccordion extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
  }

  render() {
    const template = `
            <div class="info-section">
                <button class="info-section__trigger" aria-expanded="false">
                    <span class="text-decoration-underline fw-bolder">+ Why Should I Subscribe</span>
                    <span>|</span>
                    <span class="info-text">Pause or cancel anytime</span>
                </button>
                <div class="info-section__content" aria-hidden="true">
                    <img 
                        src="${this.dataset.image}" 
                        alt="Why Subscribe"
                        width="600"
                        height="400"
                        loading="lazy"
                    >
                </div>
            </div>
        `;
    this.innerHTML = template;
  }

  setupEventListeners() {
    const trigger = this.querySelector('.info-section__trigger');
    trigger.addEventListener('click', () => this.toggle());
  }

  toggle() {
    this.isOpen = !this.isOpen;
    const content = this.querySelector('.info-section__content');
    const trigger = this.querySelector('.info-section__trigger');

    trigger.setAttribute('aria-expanded', this.isOpen);
    content.setAttribute('aria-hidden', !this.isOpen);

    if (this.isOpen) {
      content.style.maxHeight = content.scrollHeight + 'px';
    } else {
      content.style.maxHeight = '0';
    }
  }
}

customElements.define('subscription-info-accordion', SubscriptionInfoAccordion);